import { useState } from "react";
import { ArrowDown, CheckCircle2, Mail, MessageSquareText, PhoneCall, Users, XCircle, Clock, Loader2, Search, RotateCcw, Voicemail, PhoneIncoming } from "lucide-react";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";

// ---------------------------------------------------------------------------------------------
// The ladder in plain English (iter475g). Owner: "I have two people — are they both reached at the
// same time? If I add a tier, is that the next tier of people?" Yes and yes — now the page says so.
// ---------------------------------------------------------------------------------------------
const chanWords = (ch) => {
  const on = [ch?.sms !== false && "texted", ch?.call !== false && "called", !!ch?.email && "emailed"].filter(Boolean);
  return on.length > 1 ? `${on.slice(0, -1).join(", ")} and ${on[on.length - 1]}` : (on[0] || "texted");
};

function Num({ value, onChange, testid, disabled, min = 1 }) {
  // Free while typing (so the box can be cleared and retyped); clamped to `min` when it loses focus.
  const [draft, setDraft] = useState(null);
  const commit = (raw) => { const n = parseInt(raw, 10); onChange(Number.isNaN(n) ? min : Math.max(min, n)); setDraft(null); };
  return (
    <input type="number" min={min} data-testid={testid} value={draft ?? value} disabled={disabled}
      onChange={(e) => { setDraft(e.target.value); const n = parseInt(e.target.value, 10); if (!Number.isNaN(n) && n >= min) onChange(n); }}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") commit(e.currentTarget.value); }}
      className="mx-1 w-12 rounded-md border border-sand-300 bg-white px-1.5 py-0.5 text-center text-xs font-bold text-ink outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50" />
  );
}

export function LadderFlow({ tiers, responders, cfg, canEdit, onTiming }) {
  const nameOf = (id) => responders.find((r) => r.user_id === id)?.name || "someone";
  const method = cfg.unacked_method || "both";
  const nudge = method === "off" ? null : method === "retext" ? "re-texted" : method === "call" ? "called again" : "re-texted and called again";
  return (
    <div data-testid="ladder-flow" className="mt-4 rounded-2xl border border-ink/10 bg-sand-50/70 p-4">
      <div className="text-[11px] font-bold uppercase tracking-wide text-ink/50">How this ladder runs</div>
      <ol className="mt-3 space-y-0">
        {tiers.map((t, i) => {
          const names = (t.user_ids || []).map(nameOf);
          const isLast = i === tiers.length - 1;
          return (
            <li key={i} data-testid={`ladder-flow-step-${i}`}>
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-bold text-white">{i + 1}</span>
                <div className="min-w-0 text-sm text-ink">
                  <span className="font-semibold">{t.name || `Step ${i + 1}`}</span>
                  {names.length === 0 ? (
                    <span className="text-amber-700"> — nobody on this step yet{i === 0 ? ` (falls back to every verified manager and ${V.lc("board")})` : ""}</span>
                  ) : (
                    <span> — <span className="font-medium">{names.join(", ")}</span> {names.length > 1 ? "are all" : "is"} {chanWords(t.channels)}{i === 0 ? " the moment it fires" : ""}.</span>
                  )}
                </div>
              </div>
              {!isLast && (
                <div className="ml-3 flex items-center gap-2 border-l-2 border-dashed border-ink/15 py-2 pl-5 text-xs text-ink/65" data-testid={`ladder-flow-gap-${i}`}>
                  <ArrowDown className="h-3.5 w-3.5 shrink-0" /> nobody replies ACK within
                  <Num value={t.wait_minutes ?? 7} disabled={!canEdit} testid={`ladder-flow-wait-${i}`} onChange={(v) => onTiming({ tierWait: [i, v] })} />
                  min → Step {i + 2} is brought in <span className="text-ink/45">(Step {i + 1} stays on it)</span>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-3 space-y-1.5 border-t border-ink/10 pt-3 text-xs text-ink/65">
        <div className="flex flex-wrap items-center gap-1" data-testid="ladder-flow-alert-order">
          <PhoneCall className="h-3.5 w-3.5 shrink-0" /> When someone is paged, by default they are
          <select data-testid="ladder-flow-alert-order-select" value={cfg.alert_order || "call_then_text"} disabled={!canEdit}
            onChange={(e) => onTiming({ alert_order: e.target.value })}
            className="mx-1 rounded-md border border-sand-300 bg-white px-1.5 py-0.5 text-xs font-bold text-ink outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50">
            <option value="call_then_text">called first — the text with the details follows about 30 s later</option>
            <option value="text_then_call">texted first — the call waits for the nudge if nobody replies ACK</option>
            <option value="together">called and texted at the same moment</option>
          </select>
          <span className="text-ink/45">Anyone on the ladder can pick their own order under Account → Notifications → Emergency alerts.</span>
        </div>
        {nudge && (
          <div className="flex flex-wrap items-center gap-1" data-testid="ladder-flow-nudge">
            <Clock className="h-3.5 w-3.5 shrink-0" /> Anyone who hasn't replied ACK after
            <Num value={cfg.call_delay_minutes ?? 7} disabled={!canEdit} testid="ladder-flow-call-delay" onChange={(v) => onTiming({ call_delay_minutes: v })} />
            min is {nudge}.{tiers.length > 1 && (cfg.nudge_retext_every_tier !== false
              ? " Each later step gets the same nudge, and everyone alerted so far is included."
              : " Later steps' nudges re-call only — re-texting happens once, at step 1.")}
          </div>
        )}
        {tiers.length > 1 && (
          <div className="flex flex-wrap items-center gap-1" data-testid="ladder-flow-drill-note">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Drills page step 1 only — a drill nobody takes within {cfg.no_ack_timeout_minutes ?? 15} min is a failed drill; it never wakes step 2.
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1" data-testid="ladder-flow-last-resort">
          <Users className="h-3.5 w-3.5 shrink-0" /> If <span className="font-semibold text-ink">nobody</span> has taken it after
          <Num value={cfg.no_ack_timeout_minutes ?? 15} disabled={!canEdit} testid="ladder-flow-timeout" onChange={(v) => onTiming({ no_ack_timeout_minutes: v })} />
          min, everyone at your company who works this {V.lc("org")} — its managers, {V.lcs("board")} and any fallback emails — is texted and emailed. Nobody outside your company.
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="ladder-flow-repeat">
          <RotateCcw className="h-3.5 w-3.5 shrink-0" /> Still nobody? Everyone alerted is {nudge || "re-texted"} and staff are re-emailed every
          <Num value={cfg.unacked_repeat_minutes ?? 10} disabled={!canEdit} testid="ladder-flow-repeat-every" onChange={(v) => onTiming({ unacked_repeat_minutes: v })} />
          min, up to
          <Num value={cfg.unacked_repeat_max ?? 6} min={0} disabled={!canEdit} testid="ladder-flow-repeat-max" onChange={(v) => onTiming({ unacked_repeat_max: v })} />
          more times. Then the texts and calls stop, staff get one final "nobody acknowledged" email, and the record stays open until someone takes it.
        </div>
        <div className="text-ink/50">The first person to reply <span className="font-semibold text-ink/70">ACK</span> (or press 1 on the call) owns it; everyone else gets a stand-down text.</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Who's been contacted — per person, live, on the active emergency and in the conversation lane.
// ---------------------------------------------------------------------------------------------
const deliveryWord = (d) => ({ delivered: "delivered", sent: "sent", queued: "sending", failed: "failed", undelivered: "failed", deferred: "follows the call", call: "", email: "" }[d] ?? d ?? "");

export function ContactedPanel({ ev, compact = false }) {
  const alerts = ev?.alerts || [];
  if (!alerts.length) {
    return ev?.no_responders
      ? <p data-testid="contacted-none" className="text-xs font-semibold text-red-700">Nobody could be reached — no responder has a verified mobile.</p>
      : <p data-testid="contacted-none" className="text-xs text-ink/55">Nobody alerted yet.</p>;
  }
  const ackId = ev?.acked_by?.user_id;
  const rows = [...alerts].sort((a, b) => (a.tier ?? 0) - (b.tier ?? 0));
  return (
    <div data-testid="contacted-panel" className={compact ? "space-y-1" : "space-y-1.5"}>
      {!compact && <div className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Who's been contacted</div>}
      {rows.map((a, i) => {
        const texted = (a.channel || "sms").includes("sms");
        const failed = ["failed", "undelivered"].includes(a.delivery);
        const acked = ackId && a.user_id === ackId;
        return (
          <div key={`${a.user_id}-${a.phone}-${i}`} data-testid={`contacted-${a.user_id}`}
            className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-white/80 px-2.5 py-1.5 text-xs ${acked ? "ring-1 ring-emerald-300" : ""}`}>
            <span className="rounded-full bg-ink px-1.5 py-px text-[10px] font-bold text-white">{(a.tier ?? 0) + 1}</span>
            <span className="font-semibold text-ink">{a.name}</span>
            {a.role_label && <span className="text-ink/50">{a.role_label}</span>}
            <span className="ml-auto flex flex-wrap items-center gap-1.5">
              {texted && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-px font-semibold ${failed ? "bg-red-50 text-red-700" : a.delivery === "delivered" ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-ink/60"}`}>
                  <MessageSquareText className="h-3 w-3" /> text {deliveryWord(a.delivery) || "sent"}
                </span>
              )}
              {a.email_ok && <span className="inline-flex items-center gap-1 rounded-full bg-sand-100 px-2 py-px font-semibold text-ink/60"><Mail className="h-3 w-3" /> emailed</span>}
              {a.called_at
                ? <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-px font-semibold text-sky-700"><PhoneCall className="h-3 w-3" /> called{(a.call_count || 0) > 1 ? ` ×${a.call_count}` : ""}</span>
                : a.call_ok ? <span className="inline-flex items-center gap-1 rounded-full bg-sand-100 px-2 py-px font-semibold text-ink/50"><PhoneCall className="h-3 w-3" /> call pending</span>
                : !a.verified ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-px font-semibold text-amber-700">not verified · no call</span> : null}
              {a.last_call_outcome === "voicemail" && (
                <span data-testid={`contacted-voicemail-${a.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-px font-semibold text-amber-700">
                  <Voicemail className="h-3 w-3" /> voicemail{(a.voicemail_count || 0) > 1 ? ` ×${a.voicemail_count}` : ""}
                </span>
              )}
              {a.last_call_outcome === "answered" && (
                <span data-testid={`contacted-answered-${a.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-px font-semibold text-emerald-700">
                  <PhoneIncoming className="h-3 w-3" /> answered
                </span>
              )}
              {acked
                ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-px font-bold text-white"><CheckCircle2 className="h-3 w-3" /> took it</span>
                : ackId ? <span className="inline-flex items-center gap-1 text-ink/45"><XCircle className="h-3 w-3" /> stood down</span>
                : <span className="text-ink/45">no reply yet</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// "Would this message trigger?" — try a phrase against the words on screen (unsaved edits included).
// ---------------------------------------------------------------------------------------------
export function PhraseTester({ tenantId, keywords, criticalKeywords, aiBackstop }) {
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!text.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post(`/tenants/${tenantId}/emergency/test-phrase`, { text, keywords, critical_keywords: criticalKeywords, ai_backstop: aiBackstop });
      setRes(r.data);
    } catch { setRes({ error: true }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="phrase-tester" className="mt-4 rounded-xl border border-sand-200 bg-sand-50/60 p-3">
      <div className="text-xs font-bold text-ink">Test a phrase</div>
      <p className="mt-0.5 text-xs text-ink/50">Type what {V.a("person")} might text and see whether it would trigger — and which word caught it.</p>
      <div className="mt-2 flex gap-2">
        <input data-testid="phrase-tester-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="e.g. water is pouring through my ceiling" className="min-w-0 flex-1 rounded-lg border border-sand-200 bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        <button type="button" data-testid="phrase-tester-run" onClick={run} disabled={busy || !text.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Test
        </button>
      </div>
      {res && !res.error && (
        <div data-testid="phrase-tester-result" className={`mt-2 rounded-lg px-3 py-2 text-xs ${res.hit ? (res.critical ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-800") : "bg-white text-ink/60 border border-sand-200"}`}>
          {res.hit
            ? <>Would trigger{res.critical ? " as CRITICAL" : ""} — {res.via === "keyword" ? <>matched <b>“{res.keyword}”</b>{res.critical_keyword ? <> (critical word <b>“{res.critical_keyword}”</b>)</> : ""}</> : <>no word matched, the <b>AI backstop</b> caught it</>}. {res.confirm ? `The ${V.lc("person")} is asked to confirm first.` : "The team is alerted immediately, no confirmation."}</>
            : <>Would <b>not</b> trigger — no word matched{aiBackstop === false ? " (AI backstop is off)" : " and the AI didn't read it as an emergency"}.</>}
        </div>
      )}
      {res?.error && <p className="mt-2 text-xs text-red-700">Couldn't run the test.</p>}
    </div>
  );
}

export function ResetWordsButton({ onReset, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onReset}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/55 hover:text-ink">
      <RotateCcw className="h-3 w-3" /> Reset to defaults
    </button>
  );
}
